import { setDefaultHeader } from '@/publicSearch/graphql/headers';
import { APOLLO_ERROR_POLICY, APOLLO_FETCH_POLICY } from '@/constants/apollo';
import {
  GetAnimalEbvsQuery,
  useGetAnimalEbvsQuery,
} from '@/publicSearch/graphql/queries.generated';
import {
  FieldOption,
  LogicalOperator,
  OperatorOption,
} from '@/components/publicSearch/constants';
import type {
  AnimalEbvFilterField,
  ComparisonOperator,
} from '@/graphqlClient/types';

type AnimalEbvItem = GetAnimalEbvsQuery['animalEbvs']['items'][number];
type AnimalEbvDocument = AnimalEbvItem['documents'][number];

type AnimalViewDto = {
  animalId: number;
  animalIdent: string | null;
  animalName: string | null;
  animalStatus: string | null;
  dateOfBirth: string | null;
  registrationStatus: string | null;
  sex: string | null;
  tattoo: string | null;
  societyId?: number | null;
  documents: AnimalEbvDocument[];
  breeder?: {
    memberId: number;
    memberIdent?: string | null;
    memberName: string;
    detailsAvailFlag?: string | null;
  };
  currentOwner?: {
    memberId: number;
    memberIdent?: string | null;
    memberName: string;
    detailsAvailFlag?: string | null;
  };
  sire?: { animalId: number; animalName: string | null };
  dam?: { animalId: number; animalName: string | null };
  ebvRaw?: AnimalEbvItem;
};

const toAnimalViewDto = (animal: AnimalEbvItem): AnimalViewDto => ({
  animalId: animal.animalId,
  animalIdent: animal.animalIdent ?? null,
  animalName: animal.animalName ?? null,
  animalStatus: animal.animalStatus ?? null,
  dateOfBirth: animal.dateOfBirth ? String(animal.dateOfBirth) : null,
  registrationStatus: animal.registrationStatus ?? null,
  sex: animal.sex ?? null,
  tattoo: animal.animalTattoo ?? null,
  societyId: animal.societyId ?? null,
  documents: animal.documents ?? [],
  breeder: animal.breederId
    ? {
        memberId: animal.breederId,
        memberName: animal.breederName ?? '',
        detailsAvailFlag: animal.breederDetailsAvailFlag ?? null,
      }
    : undefined,
  currentOwner: animal.currentOwnerId
    ? {
        memberId: animal.currentOwnerId,
        memberName: animal.currentOwnerName ?? '',
        detailsAvailFlag: animal.currentOwnerDetailsAvailFlag ?? null,
      }
    : undefined,
  sire: animal.sireId
    ? {
        animalId: animal.sireId,
        animalName: animal.sireName ?? null,
      }
    : undefined,
  dam: animal.damId
    ? {
        animalId: animal.damId,
        animalName: animal.damName ?? null,
      }
    : undefined,
  ebvRaw: animal,
});

export function useAnimal(animalId: string, orgId: string) {
  const { data, error, loading, previousData } = useGetAnimalEbvsQuery({
    variables: {
      requestInput: {
        pagination: {
          currentPage: 1,
          pageSize: 1,
        },
        filterGroups: [
          {
            filters: [
              {
                field: FieldOption.animalId.value as AnimalEbvFilterField,
                operator: OperatorOption.equals.value as ComparisonOperator,
                value: animalId,
              },
            ],
            groups: [],
            logicalOperator: LogicalOperator.and,
          },
        ],
        sortOptions: [],
      },
    },
    context: {
      headers: setDefaultHeader(orgId),
    },
    skip: !orgId || !animalId || isNaN(Number(animalId)),
    errorPolicy: APOLLO_ERROR_POLICY.ALL,
    fetchPolicy: APOLLO_FETCH_POLICY.NETWORK_ONLY,
    notifyOnNetworkStatusChange: true,
  });

  const animal = data?.animalEbvs.items[0]
    ? toAnimalViewDto(data.animalEbvs.items[0])
    : null;

  // The query succeeded but matched no animal. Distinct from "still loading"
  // and from "transient error" — used by callers to render a Not Found state.
  const notFound = !loading && !!data && !animal && !error;

  return {
    data: animal ? { animal } : null,
    previousData: previousData?.animalEbvs.items[0]
      ? { animal: toAnimalViewDto(previousData.animalEbvs.items[0]) }
      : null,
    error,
    loading,
    notFound,
    done: (data || error) && !loading,
  };
}
